import React, { useRef, useEffect, useState } from "react";
import { Sun, Moon, Compass, Map, Upload, EyeOff, Eye, Settings2, Menu, ChevronDown, GripVertical } from "lucide-react";
import AdminGate from "./AdminGate";
import { useAdmin } from "../contexts/AdminContext";

const LOGO = "https://static.prod-images.emergentagent.com/jobs/7370f804-5e99-4623-a9fe-ba3d6cce18de/images/9ad81d35a64e8cadd24fbfe269d9336faadb779fbd9cacc5171b7d425bdc9fb3.png";

export default function Header({
    sections,
    currentTab,
    onTabChange,
    isNight,
    onToggleDayNight,
    isIso,
    onToggleIso,
    onOpenUpload,
    menuHidden = false,
    onToggleMenu,
    onOpenAdminPanel,
}) {
    const stripRef = useRef(null);
    const headerRef = useRef(null);
    const [showHint, setShowHint] = useState(true);
    const [drawerOpen, setDrawerOpen] = useState(false);
    const { isAdmin, setGateOpen } = useAdmin();

    // Persisted vertical offset so viewers can slide the whole menu bar up/down
    // and stop it from blocking on-screen buttons. Snap back to 0 on double-click.
    const [menuY, setMenuY] = useState(() => {
        try { return parseInt(localStorage.getItem("caprio_menu_y") || "0", 10) || 0; } catch (_e) { return 0; }
    });
    const dragRef = useRef({ startY: 0, startMenuY: 0, dragging: false });

    const clampY = (y) => {
        try {
            const h = headerRef.current?.offsetHeight || 80;
            const max = Math.max(0, window.innerHeight - h - 8);
            return Math.min(max, Math.max(0, y));
        } catch (_e) {
            return Math.max(0, y);
        }
    };

    const persistY = (y) => {
        try { localStorage.setItem("caprio_menu_y", String(y)); } catch (_e) { /* noop */ }
    };

    const onDragMove = (clientY) => {
        const d = dragRef.current;
        if (!d.dragging) return;
        const delta = clientY - d.startY;
        setMenuY(clampY(d.startMenuY + delta));
    };
    const onDragEnd = () => {
        const d = dragRef.current;
        if (!d.dragging) return;
        d.dragging = false;
        setMenuY((y) => { persistY(y); return y; });
        window.removeEventListener("mousemove", onMouseMove);
        window.removeEventListener("mouseup", onMouseUp);
        window.removeEventListener("touchmove", onTouchMove);
        window.removeEventListener("touchend", onTouchEnd);
    };
    const onMouseMove = (e) => onDragMove(e.clientY);
    const onMouseUp = () => onDragEnd();
    const onTouchMove = (e) => { if (e.touches[0]) onDragMove(e.touches[0].clientY); };
    const onTouchEnd = () => onDragEnd();

    const beginDrag = (clientY) => {
        dragRef.current = { startY: clientY, startMenuY: menuY, dragging: true };
        window.addEventListener("mousemove", onMouseMove);
        window.addEventListener("mouseup", onMouseUp);
        window.addEventListener("touchmove", onTouchMove, { passive: false });
        window.addEventListener("touchend", onTouchEnd);
    };
    const onGripMouseDown = (e) => { e.preventDefault(); beginDrag(e.clientY); };
    const onGripTouchStart = (e) => {
        if (!e.touches[0]) return;
        beginDrag(e.touches[0].clientY);
    };
    const resetMenuY = () => { setMenuY(0); persistY(0); };

    // Re-clamp on window resize so the header never disappears off-screen.
    useEffect(() => {
        const onResize = () => setMenuY((y) => {
            const cy = clampY(y);
            if (cy !== y) persistY(cy);
            return cy;
        });
        window.addEventListener("resize", onResize);
        return () => window.removeEventListener("resize", onResize);
    }, []);

    // Silent 5-click gesture on the Caprio logo opens admin gate. No visible counter.
    const logoClickRef = useRef({ count: 0, timer: null });
    const handleLogoClick = () => {
        const c = logoClickRef.current;
        if (c.timer) clearTimeout(c.timer);
        c.count += 1;
        if (c.count >= 5) {
            c.count = 0;
            if (!isAdmin) setGateOpen(true);
            return;
        }
        c.timer = setTimeout(() => { c.count = 0; }, 1500);
    };

    useEffect(() => {
        const t = setTimeout(() => setShowHint(false), 7000);
        return () => clearTimeout(t);
    }, []);

    // Close drawer on tab change
    useEffect(() => { setDrawerOpen(false); }, [currentTab]);

    // Always include Portals as the special final tab
    const visibleSections = sections.filter((s) => !s.hidden_in_menu);
    const allTabs = [
        ...visibleSections,
        { id: "portals", label: "Portals", special: "glowing" },
    ];

    const labelOf = (tab) => tab.custom_label || tab.label;
    const activeLabel = labelOf(allTabs.find((t) => t.id === currentTab) || { label: "Menu" });

    const accent = isNight ? "#D4AF37" : "#B89B5E";
    const textBase = isNight ? "text-white/70" : "text-black/70";
    const textActive = isNight ? "text-white" : "text-black";

    return (
        <header
            ref={headerRef}
            data-testid="chateau-header"
            className="pointer-events-none absolute top-0 left-0 right-0 z-40 px-3 md:px-8 pt-3 md:pt-6"
            style={{ transform: `translateY(${menuY}px)`, transition: dragRef.current.dragging ? "none" : "transform 220ms ease-out" }}
        >
            <div className={`pointer-events-auto rounded-2xl md:rounded-3xl ${isNight ? "glass" : "glass-day"} px-3 md:px-6 py-2 md:py-4 flex items-center gap-2 md:gap-4 fade-in`}>
                {/* Logo / Brand */}
                <div
                    className="flex items-center gap-2 md:gap-3 shrink-0 cursor-pointer select-none"
                    data-testid="brand-mark"
                    onClick={handleLogoClick}
                    title=""
                >
                    <div
                        className="w-9 h-9 md:w-11 md:h-11 rounded-full grid place-items-center"
                        style={{
                            background: isNight
                                ? "radial-gradient(circle, rgba(212,175,55,0.18), rgba(212,175,55,0) 70%)"
                                : "radial-gradient(circle, rgba(184,155,94,0.20), rgba(184,155,94,0) 70%)",
                            boxShadow: `inset 0 0 0 1px ${isNight ? "rgba(212,175,55,0.45)" : "rgba(184,155,94,0.55)"}`,
                        }}
                    >
                        <img src={LOGO} alt="Caprio Chateau" className="w-6 h-6 md:w-7 md:h-7 object-contain" />
                    </div>
                    <div className="hidden sm:flex flex-col leading-tight">
                        <span
                            className="font-serif-display text-xl md:text-2xl tracking-luxe uppercase"
                            style={{ color: isNight ? "#fff" : "#0a0a0a" }}
                        >
                            Caprio
                        </span>
                        <span
                            className="text-[10px] tracking-[0.35em] uppercase"
                            style={{ color: accent }}
                        >
                            Chateau
                        </span>
                    </div>
                </div>

                {/* Desktop tab strip (lg+ only so landscape phones use the mobile bar) */}
                {!menuHidden && (
                    <nav
                        ref={stripRef}
                        className="hidden lg:flex flex-1 items-center gap-1 lg:gap-2 overflow-x-auto no-scrollbar"
                        data-testid="tabs-strip"
                    >
                        {allTabs.map((tab) => {
                            const active = currentTab === tab.id;
                            const isPortals = tab.id === "portals";
                            return (
                                <button
                                    key={tab.id}
                                    data-testid={`tab-${tab.id}`}
                                    onClick={() => onTabChange(tab.id)}
                                    className={`relative shrink-0 px-3 lg:px-4 py-2 rounded-full text-[11px] lg:text-xs uppercase tracking-[0.22em] transition-all duration-300 ${active ? textActive : textBase}`}
                                    style={{
                                        color: isPortals ? accent : (active ? (isNight ? "#fff" : "#0a0a0a") : undefined),
                                        background: active
                                            ? (isNight ? "rgba(212,175,55,0.10)" : "rgba(184,155,94,0.14)")
                                            : "transparent",
                                        border: active
                                            ? `1px solid ${accent}55`
                                            : "1px solid transparent",
                                    }}
                                >
                                    <span className={isPortals ? "portal-glow" : ""}>
                                        {labelOf(tab)}
                                    </span>
                                    {active && (
                                        <span
                                            className="absolute left-3 right-3 -bottom-[3px] h-[1.5px]"
                                            style={{ background: accent, boxShadow: `0 0 10px ${accent}` }}
                                        />
                                    )}
                                </button>
                            );
                        })}
                    </nav>
                )}
                {menuHidden && <div className="flex-1 hidden lg:block" />}

                {/* Mobile / landscape / tablet: active-tab pill dropdown */}
                <button
                    type="button"
                    data-testid="mobile-current-tab"
                    onClick={() => setDrawerOpen((v) => !v)}
                    className="lg:hidden flex-1 min-w-0 text-center px-3 py-2 rounded-full text-[11px] uppercase tracking-[0.22em] truncate inline-flex items-center justify-center gap-2"
                    style={{
                        background: isNight ? "rgba(212,175,55,0.10)" : "rgba(184,155,94,0.14)",
                        color: isNight ? "#fff" : "#0a0a0a",
                        border: `1px solid ${accent}55`,
                    }}
                    title="Open menu"
                >
                    <span className="truncate">{activeLabel}</span>
                    <ChevronDown size={12} style={{ transform: drawerOpen ? "rotate(180deg)" : "none", transition: "transform 0.2s", color: accent }} />
                </button>

                {/* Right toggles */}
                <div className="flex items-center gap-1.5 md:gap-2 shrink-0">
                    {/* Drag handle — move the whole menu up/down; double-click resets */}
                    <button
                        type="button"
                        data-testid="menu-drag-handle"
                        onMouseDown={onGripMouseDown}
                        onTouchStart={onGripTouchStart}
                        onDoubleClick={resetMenuY}
                        className="relative w-8 h-9 md:w-9 md:h-11 rounded-full grid place-items-center transition-all touch-none select-none"
                        style={{
                            background: isNight ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.06)",
                            border: `1px solid ${accent}50`,
                            color: accent,
                            cursor: dragRef.current.dragging ? "grabbing" : "grab",
                        }}
                        title="Drag to move menu · double-click to reset"
                        aria-label="Drag to move the menu vertically"
                    >
                        <GripVertical size={14} />
                    </button>

                    <AdminGate isNight={isNight} silent />

                    {/* Admin Panel gear — always visible when admin */}
                    {isAdmin && onOpenAdminPanel && (
                        <button
                            data-testid="open-admin-panel"
                            onClick={onOpenAdminPanel}
                            className="relative w-9 h-9 md:w-11 md:h-11 rounded-full grid place-items-center transition-all hover:scale-[1.05]"
                            style={{
                                background: accent,
                                color: "#0a0a0a",
                                border: `1px solid ${accent}`,
                                boxShadow: `0 0 18px ${accent}aa`,
                            }}
                            title="Admin Panel"
                        >
                            <Settings2 size={14} />
                        </button>
                    )}

                    {/* Day / Night toggle — always visible on all breakpoints */}
                    <button
                        data-testid="day-night-toggle"
                        onClick={onToggleDayNight}
                        className="relative w-9 h-9 lg:w-11 lg:h-11 rounded-full grid place-items-center transition-all"
                        style={{
                            background: isNight ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.06)",
                            border: `1px solid ${accent}50`,
                            color: accent,
                        }}
                        title={isNight ? "Switch to Day" : "Switch to Night"}
                    >
                        {isNight ? <Moon size={16} /> : <Sun size={16} />}
                    </button>

                    {/* Desktop-only extra toggles (lg+ so landscape phones keep it compact) */}
                    <div className="hidden lg:flex items-center gap-2">
                        {onToggleMenu && (
                            <button
                                data-testid="menu-toggle-btn"
                                onClick={onToggleMenu}
                                className="relative w-11 h-11 rounded-full grid place-items-center transition-all"
                                style={{
                                    background: isNight ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.06)",
                                    border: `1px solid ${accent}50`,
                                    color: accent,
                                }}
                                title={menuHidden ? "Show menu" : "Hide menu"}
                            >
                                {menuHidden ? <Eye size={16} /> : <EyeOff size={16} />}
                            </button>
                        )}

                        {onOpenUpload && isAdmin && currentTab !== "portals" && (
                            <button
                                data-testid="open-upload-btn"
                                onClick={onOpenUpload}
                                className="relative w-11 h-11 rounded-full grid place-items-center transition-all hover:scale-[1.05]"
                                style={{
                                    background: isNight ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.06)",
                                    border: `1px solid ${accent}50`,
                                    color: accent,
                                }}
                                title="Upload images for this chamber"
                            >
                                <Upload size={16} />
                            </button>
                        )}

                        <div
                            data-testid="iso-360-toggle"
                            onClick={onToggleIso}
                            className="relative cursor-pointer rounded-full p-1 flex items-center gap-1 transition-colors"
                            style={{
                                background: isNight ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.06)",
                                border: `1px solid ${accent}40`,
                            }}
                            title={isIso ? "Switch to 360°" : "Switch to ISO map"}
                        >
                            <div
                                className="px-2 py-1 rounded-full flex items-center gap-1 transition-all"
                                style={{
                                    background: !isIso ? accent : "transparent",
                                    color: !isIso ? "#0a0a0a" : (isNight ? "#fff" : "#0a0a0a"),
                                }}
                            >
                                <Compass size={12} />
                                <span className="text-[10px] tracking-[0.2em] uppercase">360°</span>
                            </div>
                            <div
                                className="px-2 py-1 rounded-full flex items-center gap-1 transition-all"
                                style={{
                                    background: isIso ? accent : "transparent",
                                    color: isIso ? "#0a0a0a" : (isNight ? "#fff" : "#0a0a0a"),
                                }}
                            >
                                <Map size={12} />
                                <span className="text-[10px] tracking-[0.2em] uppercase">ISO</span>
                            </div>
                        </div>
                    </div>

                    {/* Mobile hamburger (lg-) */}
                    <button
                        type="button"
                        data-testid="mobile-menu-btn"
                        onClick={() => setDrawerOpen((v) => !v)}
                        className="lg:hidden relative w-9 h-9 rounded-full grid place-items-center transition-all"
                        style={{
                            background: isNight ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.06)",
                            border: `1px solid ${accent}50`,
                            color: accent,
                        }}
                        title="Open menu"
                    >
                        <Menu size={16} />
                    </button>
                </div>
            </div>

            {/* Hint */}
            {showHint && (
                <div
                    className={`hidden lg:block pointer-events-none mt-3 text-center text-[10px] tracking-[0.4em] uppercase fade-in`}
                    style={{ color: isNight ? "rgba(255,255,255,0.45)" : "rgba(0,0,0,0.5)" }}
                    data-testid="header-hint"
                >
                    drag to look around · scroll to zoom · double-click iso to reset
                </div>
            )}

            {/* Mobile drawer */}
            {/* Mobile/landscape/tablet dropdown — anchored right under the header pill */}
            {drawerOpen && (
                <div
                    className="lg:hidden fixed inset-0 z-[60] pointer-events-auto"
                    data-testid="mobile-drawer"
                >
                    <div
                        className="absolute inset-0"
                        onClick={() => setDrawerOpen(false)}
                        data-testid="mobile-drawer-backdrop"
                    />
                    <div
                        className="absolute left-3 right-3 lg:hidden fade-in"
                        style={{ top: "70px" }}
                    >
                        <div
                            className={`rounded-2xl p-3 max-h-[70vh] flex flex-col`}
                            style={{
                                background: isNight ? "rgba(14,14,18,0.96)" : "rgba(250,246,235,0.98)",
                                border: `1px solid ${accent}55`,
                                boxShadow: `0 20px 50px rgba(0,0,0,0.55)`,
                                backdropFilter: "blur(14px)",
                            }}
                        >
                            {/* View mode toggle */}
                            <div
                                className="mb-3 rounded-full p-1 flex items-center gap-1"
                                style={{
                                    background: isNight ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.06)",
                                    border: `1px solid ${accent}40`,
                                }}
                                data-testid="mobile-iso-360-toggle"
                                onClick={onToggleIso}
                            >
                                <div
                                    className="flex-1 text-center px-3 py-1.5 rounded-full flex items-center justify-center gap-1"
                                    style={{
                                        background: !isIso ? accent : "transparent",
                                        color: !isIso ? "#0a0a0a" : (isNight ? "#fff" : "#0a0a0a"),
                                    }}
                                >
                                    <Compass size={13} />
                                    <span className="text-[10px] tracking-[0.2em] uppercase">360°</span>
                                </div>
                                <div
                                    className="flex-1 text-center px-3 py-1.5 rounded-full flex items-center justify-center gap-1"
                                    style={{
                                        background: isIso ? accent : "transparent",
                                        color: isIso ? "#0a0a0a" : (isNight ? "#fff" : "#0a0a0a"),
                                    }}
                                >
                                    <Map size={13} />
                                    <span className="text-[10px] tracking-[0.2em] uppercase">ISO</span>
                                </div>
                            </div>

                            {/* Tabs list — scrollable */}
                            <div className="flex-1 overflow-y-auto -mx-1 px-1" data-testid="mobile-tabs-list">
                                {allTabs.map((tab) => {
                                    const active = currentTab === tab.id;
                                    const isPortals = tab.id === "portals";
                                    return (
                                        <button
                                            key={tab.id}
                                            data-testid={`mobile-tab-${tab.id}`}
                                            onClick={() => { onTabChange(tab.id); setDrawerOpen(false); }}
                                            className="w-full text-left px-3 py-2 rounded-lg text-[12px] uppercase tracking-[0.22em] mb-1 transition-all"
                                            style={{
                                                color: isPortals ? accent : (active ? (isNight ? "#fff" : "#0a0a0a") : (isNight ? "rgba(255,255,255,0.75)" : "rgba(0,0,0,0.75)")),
                                                background: active
                                                    ? (isNight ? "rgba(212,175,55,0.12)" : "rgba(184,155,94,0.16)")
                                                    : "transparent",
                                                border: active ? `1px solid ${accent}55` : "1px solid transparent",
                                            }}
                                        >
                                            <span className={isPortals ? "portal-glow" : ""}>
                                                {labelOf(tab)}
                                            </span>
                                        </button>
                                    );
                                })}
                            </div>

                            {/* Admin quick actions */}
                            {isAdmin && (
                                <div className="pt-3 mt-3 space-y-2" style={{ borderTop: `1px solid ${accent}30` }}>
                                    {onOpenAdminPanel && (
                                        <button
                                            type="button"
                                            onClick={() => { onOpenAdminPanel(); setDrawerOpen(false); }}
                                            data-testid="mobile-open-admin-panel"
                                            className="w-full inline-flex items-center justify-center gap-2 px-4 py-2 rounded-full text-[10px] tracking-[0.3em] uppercase"
                                            style={{ background: accent, color: "#0a0a0a" }}
                                        >
                                            <Settings2 size={12} /> Admin Panel
                                        </button>
                                    )}
                                    {onOpenUpload && currentTab !== "portals" && (
                                        <button
                                            type="button"
                                            onClick={() => { onOpenUpload(); setDrawerOpen(false); }}
                                            data-testid="mobile-open-upload"
                                            className="w-full inline-flex items-center justify-center gap-2 px-4 py-2 rounded-full text-[10px] tracking-[0.3em] uppercase"
                                            style={{
                                                background: "transparent",
                                                color: accent,
                                                border: `1px solid ${accent}55`,
                                            }}
                                        >
                                            <Upload size={12} /> Upload Image
                                        </button>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </header>
    );
}
